import React, { useEffect, useMemo, useState } from "react";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/primitives";
import { Plus, X, Play, Trash, Robot, Toggle } from "@phosphor-icons/react";

const BLANK = {
  name: "", trigger: "low_stock", condition: {},
  action: "notify", action_params: {}, active: true, schedule_time: "",
};

function fmtWhen(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
}

export default function AutomationRules() {
  const [rows, setRows] = useState([]);
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // "new" | rule
  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        api.get("/automation/rules"),
        api.get("/automation/schema"),
      ]);
      setRows(r.data || []);
      setSchema(s.data);
    } catch (e) { toast.error(formatError(e)); }
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  const trigMeta = schema?.triggers?.[form.trigger];
  const actMeta = schema?.actions?.[form.action];

  useEffect(() => {
    // Reset condition/params to defaults when trigger/action changes
    if (!schema) return;
    const trigger = schema.triggers[form.trigger];
    const action = schema.actions[form.action];
    if (!modal || modal !== "new") return;
    const cond = {};
    (trigger?.condition_fields || []).forEach((f) => { cond[f.key] = f.default; });
    const params = {};
    (action?.params || []).forEach((f) => { params[f.key] = f.default; });
    setForm((f) => ({ ...f, condition: cond, action_params: params }));
    // eslint-disable-next-line
  }, [form.trigger, form.action, schema]);

  async function save() {
    if (!form.name) return toast.error("Name is required");
    setBusy(true);
    try {
      const payload = {
        name: form.name, trigger: form.trigger, condition: form.condition,
        action: form.action, action_params: form.action_params, active: form.active,
      };
      let ruleId;
      if (modal === "new") {
        const { data } = await api.post("/automation/rules", payload);
        ruleId = data.id;
        toast.success("Rule created");
      } else {
        await api.put(`/automation/rules/${modal.id}`, payload);
        ruleId = modal.id;
        toast.success("Rule updated");
      }
      // Save schedule separately (dedicated endpoint)
      await api.put(`/automation/rules/${ruleId}/schedule`,
                    { time: form.schedule_time || null });
      setModal(null); setForm(BLANK);
      await load();
    } catch (e) { toast.error(formatError(e)); }
    setBusy(false);
  }

  async function run(id) {
    try {
      const { data } = await api.post(`/automation/rules/${id}/run`);
      toast.success(`Rule ran — ${data.matches} match${data.matches===1?"":"es"}`);
      await load();
    } catch (e) { toast.error(formatError(e)); }
  }

  async function toggleActive(rule) {
    try {
      await api.put(`/automation/rules/${rule.id}`, { ...rule, active: !rule.active });
      await load();
    } catch (e) { toast.error(formatError(e)); }
  }

  async function del(id) {
    if (!window.confirm("Delete rule?")) return;
    try { await api.delete(`/automation/rules/${id}`); await load(); }
    catch (e) { toast.error(formatError(e)); }
  }

  function openEdit(rule) {
    setForm({
      name: rule.name, trigger: rule.trigger, condition: rule.condition || {},
      action: rule.action, action_params: rule.action_params || {}, active: rule.active,
      schedule_time: rule.schedule?.time || "",
    });
    setModal(rule);
  }

  return (
    <div data-testid="automation-page">
      <PageHeader
        overline="Control Room / Automation"
        title="Automation rules"
        subtitle="Wire up simple WHEN → IF → THEN so alerts and actions run without human effort."
        testId="automation-header"
      />

      <div className="phizo-card p-4 mb-4 flex items-center gap-3 flex-wrap">
        <Robot size={16} weight="bold" className="text-[#71717a]" />
        <span className="text-xs text-[#71717a]">
          {rows.length} rule{rows.length===1?"":"s"} · {rows.filter((r)=>r.active).length} active
        </span>
        <button onClick={() => { setForm(BLANK); setModal("new"); }}
                data-testid="automation-new-btn"
                className="ml-auto bg-black text-white px-4 py-1.5 text-xs flex items-center gap-1.5 hover:bg-[#27272a]">
          <Plus size={12} weight="bold" /> New rule
        </button>
      </div>

      <div className="phizo-card overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-[#fafafa] border-b border-[#e4e4e7]">
            <tr className="text-left">
              <th className="px-3 py-2 phizo-label text-[9px] w-8"></th>
              <th className="px-3 py-2 phizo-label text-[9px]">Rule</th>
              <th className="px-3 py-2 phizo-label text-[9px]">When</th>
              <th className="px-3 py-2 phizo-label text-[9px]">Then</th>
              <th className="px-3 py-2 phizo-label text-[9px]">Last run</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="p-6 text-center text-[#71717a]">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-[#71717a]">
                No rules yet. Click "New rule" to wire your first automation.
              </td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-[#f4f4f5] hover:bg-[#fafafa]"
                  data-testid={`automation-row-${r.id.slice(0,8)}`}>
                <td className="px-3 py-2">
                  <button onClick={() => toggleActive(r)} className={`w-9 h-5 relative ${r.active ? "bg-[#059669]" : "bg-[#d4d4d8]"}`}
                          data-testid={`automation-toggle-${r.id.slice(0,8)}`}>
                    <span className={`absolute top-0.5 w-4 h-4 bg-white transition-all ${r.active ? "left-4" : "left-0.5"}`} />
                  </button>
                </td>
                <td className="px-3 py-2 font-semibold cursor-pointer" onClick={() => openEdit(r)}>{r.name}</td>
                <td className="px-3 py-2">
                  <div className="text-[11px]">{schema?.triggers?.[r.trigger]?.label || r.trigger}</div>
                  {Object.keys(r.condition || {}).length > 0 && (
                    <div className="mono text-[10px] text-[#71717a]">
                      {Object.entries(r.condition).map(([k,v]) => `${k}=${v}`).join(", ")}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2">
                  <div className="text-[11px]">{schema?.actions?.[r.action]?.label || r.action}</div>
                  <div className="mono text-[10px] text-[#71717a] truncate max-w-[200px]">
                    {(r.action_params?.title || "").slice(0, 60)}
                  </div>
                </td>
                <td className="px-3 py-2 mono text-[11px] whitespace-nowrap">
                  <div>{fmtWhen(r.last_run_at)}</div>
                  {r.last_run_matches !== null && (
                    <div className="text-[10px] text-[#71717a]">{r.last_run_matches} match{r.last_run_matches===1?"":"es"}</div>
                  )}
                  {r.schedule?.time && (
                    <div className="mt-1 inline-block mono text-[10px] px-1.5 py-0.5 bg-[#dbeafe] text-[#1e40af]">
                      daily @ {r.schedule.time} UTC
                    </div>
                  )}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <button onClick={() => run(r.id)} title="Run now"
                          data-testid={`automation-run-${r.id.slice(0,8)}`}
                          className="border border-[#e4e4e7] px-2 py-1 text-xs hover:bg-black hover:text-white">
                    <Play size={12} weight="bold" className="inline mr-1" /> Run
                  </button>
                  <button onClick={() => del(r.id)} title="Delete"
                          className="border border-[#e4e4e7] px-2 py-1 text-xs hover:bg-[#fee2e2] hover:text-[#991b1b] ml-1">
                    <Trash size={12} weight="bold" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && schema && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setModal(null)}>
          <div className="bg-white w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="automation-modal">
            <div className="px-5 py-3 border-b flex justify-between items-start">
              <div>
                <div className="phizo-label text-[10px] text-[#a1a1aa]">Automation</div>
                <h3 className="font-heading text-xl font-black tracking-tighter">
                  {modal === "new" ? "New rule" : "Edit rule"}
                </h3>
              </div>
              <button onClick={() => setModal(null)} className="p-1 text-[#71717a]"><X size={16} weight="bold"/></button>
            </div>
            <div className="p-5 space-y-4">
              <label className="text-xs block">
                <div className="phizo-label text-[10px] mb-1">Rule name</div>
                <input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}
                       placeholder="e.g. Alert finance when a PO exceeds K25k"
                       className="w-full border border-[#e4e4e7] px-3 py-1.5 text-sm outline-none focus:border-black"
                       data-testid="automation-form-name"/>
              </label>

              <div className="border border-[#e4e4e7] p-3 bg-[#fafafa]">
                <div className="phizo-label text-[10px] mb-2 text-[#0284c7]">1. WHEN</div>
                <select value={form.trigger} onChange={(e) => setForm({...form, trigger: e.target.value})}
                        className="w-full border border-[#e4e4e7] bg-white px-3 py-1.5 text-sm outline-none focus:border-black"
                        data-testid="automation-form-trigger">
                  {Object.entries(schema.triggers).map(([k, t]) => <option key={k} value={k}>{t.label}</option>)}
                </select>
                {(trigMeta?.condition_fields || []).map((f) => (
                  <label key={f.key} className="text-xs block mt-2">
                    <div className="phizo-label text-[10px] mb-1">{f.key}{f.help && <span className="normal-case text-[#a1a1aa] ml-1">({f.help})</span>}</div>
                    {f.type === "select" ? (
                      <select value={form.condition[f.key] ?? f.default}
                              onChange={(e) => setForm({...form, condition: {...form.condition, [f.key]: e.target.value}})}
                              className="w-full border border-[#e4e4e7] px-3 py-1.5 text-sm outline-none focus:border-black">
                        {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : (
                      <input type={f.type} value={form.condition[f.key] ?? f.default}
                             onChange={(e) => setForm({...form, condition: {...form.condition, [f.key]: e.target.type === "number" ? Number(e.target.value) : e.target.value}})}
                             className="w-full border border-[#e4e4e7] px-3 py-1.5 text-sm mono outline-none focus:border-black"/>
                    )}
                  </label>
                ))}
              </div>

              <div className="border border-[#e4e4e7] p-3 bg-[#fafafa]">
                <div className="phizo-label text-[10px] mb-2 text-[#059669]">2. THEN</div>
                <select value={form.action} onChange={(e) => setForm({...form, action: e.target.value})}
                        className="w-full border border-[#e4e4e7] bg-white px-3 py-1.5 text-sm outline-none focus:border-black"
                        data-testid="automation-form-action">
                  {Object.entries(schema.actions).map(([k, a]) => <option key={k} value={k}>{a.label}</option>)}
                </select>
                {(actMeta?.params || []).map((f) => (
                  <label key={f.key} className="text-xs block mt-2">
                    <div className="phizo-label text-[10px] mb-1">{f.key}{f.required && <span className="text-[#dc2626]"> *</span>}{f.help && <span className="normal-case text-[#a1a1aa] ml-1">({f.help})</span>}</div>
                    {f.type === "select" ? (
                      <select value={form.action_params[f.key] ?? f.default}
                              onChange={(e) => setForm({...form, action_params: {...form.action_params, [f.key]: e.target.value}})}
                              className="w-full border border-[#e4e4e7] px-3 py-1.5 text-sm outline-none focus:border-black">
                        {f.options.map((o) => <option key={o} value={o}>{o || "(any)"}</option>)}
                      </select>
                    ) : (
                      <input type={f.type} value={form.action_params[f.key] ?? ""}
                             onChange={(e) => setForm({...form, action_params: {...form.action_params, [f.key]: e.target.value}})}
                             className="w-full border border-[#e4e4e7] px-3 py-1.5 text-sm outline-none focus:border-black"/>
                    )}
                  </label>
                ))}
              </div>

              <div className="border border-[#e4e4e7] p-3 bg-[#fafafa]">
                <div className="phizo-label text-[10px] mb-2 text-[#7c3aed]">3. SCHEDULE (optional)</div>
                <div className="text-[11px] text-[#71717a] mb-2">
                  Runs once per day at this UTC time. Leave blank to only run when clicked.
                </div>
                <input type="time" value={form.schedule_time}
                       onChange={(e) => setForm({...form, schedule_time: e.target.value})}
                       className="border border-[#e4e4e7] bg-white px-3 py-1.5 text-sm mono outline-none focus:border-black"
                       data-testid="automation-schedule-time"/>
                {form.schedule_time && (
                  <button type="button" onClick={() => setForm({...form, schedule_time: ""})}
                          className="ml-2 text-[11px] text-[#71717a] underline">clear</button>
                )}
              </div>

              <label className="flex items-center gap-2 text-xs">
                <input type="checkbox" checked={form.active}
                       onChange={(e) => setForm({...form, active: e.target.checked})}/>
                Rule is active
              </label>
            </div>
            <div className="px-5 py-3 border-t flex justify-end gap-2">
              <button onClick={() => setModal(null)} className="border border-[#e4e4e7] px-4 py-1.5 text-xs hover:bg-[#f4f4f5]">Cancel</button>
              <button onClick={save} disabled={busy} data-testid="automation-save-btn"
                      className="bg-[#002FA7] text-white px-4 py-1.5 text-xs hover:bg-[#001f7a] disabled:opacity-60">
                {busy ? "Saving…" : "Save rule"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
